import React, { useState, useEffect} from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Label } from 'recharts';
import { convertSingleValue, japaneseTickFormatter } from '../utils/commonUtils';
import AxisSettingButton from '../utils/AxisSettingButton';

const LineGraph = ({ data, graph, height=500, width=null }) => {
  const [showYaxis, setShowYaxis] = useState(false);
  const [state, setState] = useState('auto');
  const [isSmallArea, setIsSmallArea] = useState(false);
  
  const showMarkersAndLabels = data.length < 6; // データ数が6未満のときだけマーカーとラベルを表示
  const lineColors = [
    'rgba(145, 186, 250, 1)', // 青
    'rgba(90, 195, 90, 1)', // 緑
    'rgba(225, 97, 50, 1)', // オレンジ
    'rgba(147, 112, 219, 1)', // 紫
    'rgba(255, 235, 59, 1)'// 黄色
  ];

  const formatTick = (tick) => {
          return convertSingleValue(tick);
      }
  
      const renderCustomLegend = () => (
        <div className="flex flex-wrap justify-center gap-4 pb-[20px]">
            {graph.names.map((series, index) => (
                <div 
                    key={index} 
                    className="flex items-center max-w-[200px]" 
                    style={{ color: lineColors[index] }}
                >
                    <div className="mr-2 flex items-center">
                        <svg width="10" height="10">
                            <circle cx="5" cy="5" r="5" fill={lineColors[index]} />
                        </svg>
                    </div>
                    <div className="text-gray-600">{series}</div>
                </div>
            ))}
        </div>
    );
    
  // カスタムラベル関数を定義
  const CustomLabel = ({ x, y, value, index, seriesIndex }) => {
    if (!showMarkersAndLabels) return null; // データ数が6以上の場合は何も表示しない
    const offset = value >= 0 ? -20 : 30;
    return (
      <text
        x={x}
        y={y + offset}
        fill="black"
        textAnchor="middle"
        fontSize={12}
      >
        {data[index].labels[seriesIndex]}  {/* 各データポイントにラベルを表示 */}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip px-3 py-2 border border-gray-300 bg-gray-600 rounded shadow text-white">
            <div className='text-white bold'>{payload[0].payload.x}</div>
          {payload.map((item, index) => (
             <div key={index} className="flex items-center space-x-2">
                <svg width="12" height="12" className="flex-shrink-0">
                    <circle
                        cx="6" // Circle center X
                        cy="6" // Circle center Y
                        r="6" // Circle radius
                        fill={lineColors[index]}
                    />
                </svg>
                <div key={index} className='text-white'>
                  {`${graph.names[index]}: ${payload[0].payload.labels[index]}`}
                </div>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };
    
  const handleLabelSettingClick = (newState) => {
    setState(newState);
}


  useEffect(() => {
      if (!width) {
          setIsSmallArea(false);
      } else {
          if (data) {
              if (width / (data.length * data[0].y.length) < 80 ) {
                  setIsSmallArea(true);
              } else {
                  setIsSmallArea(false);
              }
          }
      }
  }, [width, data]);

  useEffect(() => {
      if ((state === 'auto' && isSmallArea) || state==='hidden')  { // autoで小さいときとhiddenのときは軸を表示
          setShowYaxis(true)
      } else {
          setShowYaxis(false)
      }
  }, [state, isSmallArea])
  
  return (
    <>
      <AxisSettingButton handleLabelSettingClick={handleLabelSettingClick} labelState={state} /> 
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 20, right: 30, left: 30, bottom: 40 }}>
          <XAxis
              dataKey="x"
              tick={{ fontSize: 12, fill: graph.settings.layout.xaxis.color }}
          >
              <Label 
                  value={graph.settings.layout.xaxis.title.text}
                  position="bottom" 
                  offset={10}
                  style={{ textAnchor: 'middle', fill: graph.settings.layout.xaxis.color }}
              />
          </XAxis>
          {showYaxis && 
            <YAxis
                tick={{ fontSize: 12, fill: graph.settings.layout.yaxis.color }} 
                tickFormatter={(value) => japaneseTickFormatter(value, '')}
                >
                <Label 
                    value={graph.settings.layout.yaxis.title.text} 
                    tickFormatter={formatTick}
                    angle={-90}
                    position="left" 
                    offset={15}
                    style={{ textAnchor: 'middle', fill: graph.settings.layout.yaxis.color }}
                />
            </YAxis>
          }
          <Tooltip
            content={<CustomTooltip/>}
            cursor={{ fill: 'rgba(200, 200, 200, 0.5)' }}
          />
          <Legend content={renderCustomLegend} verticalAlign="top" align="left" />
          {graph.y.map((y, seriesIndex) => (
            <Line
              key={seriesIndex}
              type="linear"
              dataKey={`y[${seriesIndex}]`}
              stroke={lineColors[seriesIndex]}
              name={graph.names[seriesIndex]}
              strokeWidth={2}
              dot={showMarkersAndLabels ? { r: 8, fill: lineColors[seriesIndex] } : false}  
              label={!showYaxis && <CustomLabel seriesIndex={seriesIndex} />} 
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

export default LineGraph;
